import React, { useState, Fragment } from 'react';
import { ArrowUpRight, ArrowDownLeft, ChevronDown, ChevronUp, Download, Filter, Wallet, Clock, CheckCircle2, AlertCircle, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
const transactions = [{
  id: 'TX-9021',
  customer: 'Global Tech Solutions',
  date: 'Oct 14, 2023',
  retail: 1250,
  fee: 350,
  earnings: 900,
  status: 'Settled'
}, {
  id: 'TX-9020',
  customer: 'Toronto Creative Hub',
  date: 'Oct 13, 2023',
  retail: 450,
  fee: 120,
  earnings: 330,
  status: 'Processing'
}, {
  id: 'TX-9019',
  customer: 'New York FinTech',
  date: 'Oct 12, 2023',
  retail: 2100,
  fee: 550,
  earnings: 1550,
  status: 'Settled'
}, {
  id: 'TX-9018',
  customer: 'North Star Logistics',
  date: 'Oct 11, 2023',
  retail: 85,
  fee: 25,
  earnings: 60,
  status: 'Settled'
}, {
  id: 'TX-9017',
  customer: 'Vancouver Tours Ltd',
  date: 'Oct 10, 2023',
  retail: 650,
  fee: 180,
  earnings: 470,
  status: 'Failed'
}];
const payoutHistory = [{
  id: 'PO-8821',
  date: 'Oct 01, 2023',
  amount: 3200,
  method: 'Bank Transfer • **** 4291',
  status: 'Settled'
}, {
  id: 'PO-8820',
  date: 'Sep 15, 2023',
  amount: 2850,
  method: 'Bank Transfer • **** 4291',
  status: 'Settled'
}, {
  id: 'PO-8819',
  date: 'Sep 01, 2023',
  amount: 4100,
  method: 'Bank Transfer • **** 4291',
  status: 'Settled'
}];
const availableBalance = transactions.reduce((acc, tx) => acc + tx.earnings, 0);
const pendingClear = 5000; // Static value or computed separately if required
export function Transactions() {
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [payoutFreq, setPayoutFreq] = useState('Weekly');
  return <div className="space-y-6 max-w-[1600px] mx-auto" data-id="element-1978">
      <div className="flex items-center justify-between bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-1979">
        <div data-id="element-1980">
          <h1 className="text-2xl font-bold text-slate-900" data-id="element-1981">
            Transparency Hub
          </h1>
          <p className="text-sm text-gray-500 font-medium" data-id="element-1982">
            Money In: Real-time profit splits and automated payout tracking.
          </p>
        </div>
        <div className="flex gap-3" data-id="element-1983">
          <button className="px-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-bold text-gray-600 hover:bg-gray-50 transition-all flex items-center gap-2 shadow-sm" data-id="element-1984">
            <Download size={18} data-id="element-1985" />
            Export Ledger
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1986">
        {/* Payout Request Module */}
        <div className="bg-slate-900 p-8 rounded-lg text-white space-y-8 relative overflow-hidden" data-id="element-1987">
          <div className="relative z-10" data-id="element-1988">
            <div className="flex items-center justify-between mb-8" data-id="element-1989">
              <div className="p-3 bg-white/10 rounded-lg" data-id="element-1990">
                <Wallet size={24} className="text-indigo-400" data-id="element-1991" />
              </div>
              <div className="flex bg-white/5 p-1 rounded-lg border border-white/10" data-id="element-1992">
                {['Daily', 'Weekly', 'Monthly'].map(f => <button key={f} onClick={() => setPayoutFreq(f)} className={`px-3 py-1 text-[10px] font-bold rounded-md transition-all ${payoutFreq === f ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-400 hover:text-white'}`} data-id="element-1993">
                    {f}
                  </button>)}
              </div>
            </div>

            <div className="space-y-1" data-id="element-1994">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest" data-id="element-1995">
                Available for Payout
              </p>
              <h2 className="text-4xl font-bold" data-id="element-1996">
                ${availableBalance.toLocaleString()}
              </h2>
            </div>

            <div className="mt-8 space-y-4" data-id="element-1997">
              <button className="w-full py-3 bg-indigo-600 text-white rounded-lg text-sm font-bold shadow-lg shadow-indigo-500/20 hover:bg-indigo-700 transition-all" data-id="element-1998">
                Request Payout
              </button>
              <div className="space-y-2" data-id="element-1999">
                <div className="flex justify-between text-[10px] font-bold uppercase tracking-widest" data-id="element-2000">
                  <span className="text-slate-400" data-id="element-2001">Funds Clearing</span>
                  <span className="text-indigo-400" data-id="element-2002">75% Complete</span>
                </div>
                <div className="w-full bg-white/10 h-1.5 rounded-full overflow-hidden" data-id="element-2003">
                  <div className="h-full bg-indigo-500 w-3/4" data-id="element-2004"></div>
                </div>
                <p className="text-[10px] text-slate-500 font-medium" data-id="element-2005">
                  Next clearing cycle in 3 days.
                </p>
              </div>
            </div>
          </div>
          <div className="absolute -right-8 -bottom-8 opacity-5" data-id="element-2006">
            <Wallet size={200} data-id="element-2007" />
          </div>
        </div>

        {/* Transactions Table */}
        <div className="lg:col-span-2 bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden flex flex-col" data-id="element-2008">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50" data-id="element-2009">
            <h3 className="font-bold text-slate-900 text-sm" data-id="element-2010">
              Profit Split Ledger
            </h3>
            <div className="flex gap-2" data-id="element-2011">
              <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors" data-id="element-2012">
                <Filter size={18} data-id="element-2013" />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-x-auto" data-id="element-2014">
            <table className="w-full text-left border-collapse" data-id="element-2015">
              <thead data-id="element-2016">
                <tr className="text-[10px] font-bold uppercase tracking-wider text-gray-400 border-b border-gray-100" data-id="element-2017">
                  <th className="px-6 py-4" data-id="element-2018">Transaction</th>
                  <th className="px-6 py-4" data-id="element-2019">Customer</th>
                  <th className="px-6 py-4" data-id="element-2020">Profit Split</th>
                  <th className="px-6 py-4" data-id="element-2021">Status</th>
                  <th className="px-6 py-4 w-10" data-id="element-2022"></th>
                </tr>
              </thead>
              <tbody className="text-sm" data-id="element-2023">
                {transactions.map(tx => <Fragment key={tx.id} data-id="element-2024">
                    <tr onClick={() => setExpandedRow(expandedRow === tx.id ? null : tx.id)} className={`hover:bg-gray-50 cursor-pointer transition-colors group ${expandedRow === tx.id ? 'bg-indigo-50/30' : ''}`} data-id="element-2025">
                      <td className="px-6 py-4" data-id="element-2026">
                        <p className="font-bold text-slate-900" data-id="element-2027">{tx.id}</p>
                        <p className="text-[10px] font-bold text-gray-400 uppercase" data-id="element-2028">
                          {tx.date}
                        </p>
                      </td>
                      <td className="px-6 py-4 font-bold text-slate-700" data-id="element-2029">
                        {tx.customer}
                      </td>
                      <td className="px-6 py-4" data-id="element-2030">
                        <div className="flex items-center gap-2" data-id="element-2031">
                          <span className="text-gray-400 font-medium" data-id="element-2032">
                            ${tx.retail.toLocaleString()}
                          </span>
                          <span className="text-gray-300 text-[10px] font-bold" data-id="element-2033">
                            RETAIL
                          </span>
                          <ArrowRight size={12} className="text-gray-300" data-id="element-2034" />
                          <span className="text-red-400 font-medium" data-id="element-2035">
                            -${tx.fee.toLocaleString()}
                          </span>
                          <span className="text-gray-300 text-[10px] font-bold" data-id="element-2036">
                            FEE
                          </span>
                          <ArrowRight size={12} className="text-gray-300" data-id="element-2037" />
                          <span className="text-indigo-600 font-bold" data-id="element-2038">
                            ${tx.earnings.toLocaleString()}
                          </span>
                          <span className="text-indigo-300 text-[10px] font-bold uppercase" data-id="element-2039">
                            Profit
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4" data-id="element-2040">
                        <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${tx.status === 'Settled' ? 'bg-emerald-50 text-emerald-600' : tx.status === 'Processing' ? 'bg-amber-50 text-amber-600' : 'bg-red-50 text-red-600'}`} data-id="element-2041">
                          {tx.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-300 group-hover:text-indigo-400 transition-colors" data-id="element-2042">
                        {expandedRow === tx.id ? <ChevronUp size={18} data-id="element-2043" /> : <ChevronDown size={18} data-id="element-2044" />}
                      </td>
                    </tr>
                    <AnimatePresence data-id="element-2045">
                      {expandedRow === tx.id && <tr data-id="element-2046">
                          <td colSpan={5} className="p-0" data-id="element-2047">
                            <motion.div initial={{
                        height: 0,
                        opacity: 0
                      }} animate={{
                        height: 'auto',
                        opacity: 1
                      }} exit={{
                        height: 0,
                        opacity: 0
                      }} className="overflow-hidden bg-gray-50/50 border-b border-gray-100" data-id="element-2048">
                              <div className="px-12 py-6 grid grid-cols-3 gap-8" data-id="element-2049">
                                <div className="space-y-1" data-id="element-2050">
                                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest" data-id="element-2051">
                                    Retail Price
                                  </p>
                                  <p className="text-lg font-bold text-slate-900" data-id="element-2052">
                                    ${tx.retail.toLocaleString()}.00
                                  </p>
                                  <p className="text-[10px] text-gray-500 font-medium" data-id="element-2053">
                                    Paid via Stripe Canada
                                  </p>
                                </div>
                                <div className="space-y-1" data-id="element-2054">
                                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest" data-id="element-2055">
                                    NiishEngine Fee
                                  </p>
                                  <p className="text-lg font-bold text-red-500" data-id="element-2056">
                                    -${tx.fee.toLocaleString()}.00
                                  </p>
                                  <p className="text-[10px] text-gray-500 font-medium" data-id="element-2057">
                                    Infrastructure & Fulfillment
                                  </p>
                                </div>
                                <div className="space-y-1" data-id="element-2058">
                                  <p className="text-[10px] font-bold text-indigo-600 uppercase tracking-widest" data-id="element-2059">
                                    Your Net Profit
                                  </p>
                                  <p className="text-lg font-bold text-indigo-600" data-id="element-2060">
                                    ${tx.earnings.toLocaleString()}.00
                                  </p>
                                  <p className="text-[10px] text-indigo-400 font-medium" data-id="element-2061">
                                    Available in 48 hours
                                  </p>
                                </div>
                              </div>
                            </motion.div>
                          </td>
                        </tr>}
                    </AnimatePresence>
                  </Fragment>)}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Payout History */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden flex flex-col" data-id="element-2062">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50" data-id="element-2063">
          <h3 className="font-bold text-slate-900 text-sm" data-id="element-2064">Payout History</h3>
          <button className="text-xs font-bold text-indigo-600 hover:underline" data-id="element-2065">
            View All Payouts
          </button>
        </div>
        <div className="overflow-x-auto" data-id="element-2066">
          <table className="w-full text-left border-collapse" data-id="element-2067">
            <thead data-id="element-2068">
              <tr className="text-[10px] font-bold uppercase tracking-wider text-gray-400 border-b border-gray-100" data-id="element-2069">
                <th className="px-6 py-4" data-id="element-2070">Payout ID</th>
                <th className="px-6 py-4" data-id="element-2071">Date</th>
                <th className="px-6 py-4" data-id="element-2072">Amount</th>
                <th className="px-6 py-4" data-id="element-2073">Method</th>
                <th className="px-6 py-4" data-id="element-2074">Status</th>
              </tr>
            </thead>
            <tbody className="text-sm" data-id="element-2075">
              {payoutHistory.map(po => <tr key={po.id} className="hover:bg-gray-50 transition-colors" data-id="element-2076">
                  <td className="px-6 py-4 font-bold text-slate-900" data-id="element-2077">
                    {po.id}
                  </td>
                  <td className="px-6 py-4 font-medium text-gray-500" data-id="element-2078">
                    {po.date}
                  </td>
                  <td className="px-6 py-4 font-bold text-slate-900" data-id="element-2079">
                    ${po.amount.toLocaleString()}.00
                  </td>
                  <td className="px-6 py-4 text-gray-500 font-medium" data-id="element-2080">
                    {po.method}
                  </td>
                  <td className="px-6 py-4" data-id="element-2081">
                    <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${po.status === 'Settled' ? 'bg-emerald-50 text-emerald-600' : po.status === 'Processing' ? 'bg-amber-50 text-amber-600' : 'bg-red-50 text-red-600'}`} data-id="element-2082">
                      {po.status}
                    </span>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}